import React from 'react';
import { Input } from '@/components/ui/input';

interface CurrencyInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

/**
 * Currency input that displays and accepts Brazilian format (1,50 instead of 1.50).
 * Compatible with React Hook Form's register method.
 * Accepts input as formatted display (with commas) but sends numeric value to form.
 */
export const CurrencyInput = React.forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({ onChange, onBlur, value, placeholder = '0,00', ...props }, ref) => {
    const [displayValue, setDisplayValue] = React.useState<string>('');

    React.useEffect(() => {
      if (value) {
        const numValue = typeof value === 'string' ? parseFloat(value) : (typeof value === 'number' ? value : 0);
        if (!isNaN(numValue) && numValue !== 0) {
          const formatted = numValue.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
          setDisplayValue(formatted);
        } else {
          setDisplayValue('');
        }
      } else {
        setDisplayValue('');
      }
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let input = e.target.value;

      // Remove non-numeric characters except comma and dot
      input = input.replace(/[^\d,.]/g, '');

      setDisplayValue(input);

      // Parse to numeric value: accept both comma and dot as decimal separator
      const cleaned = input.replace(/\./g, '').replace(',', '.');
      const numValue = parseFloat(cleaned) || 0;

      // Create a synthetic event with the numeric value
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: String(numValue),
        },
      } as React.ChangeEvent<HTMLInputElement>;

      if (onChange) {
        onChange(syntheticEvent);
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      // Format on blur if there's a value
      if (displayValue) {
        const cleaned = displayValue.replace(/\./g, '').replace(',', '.');
        const numValue = parseFloat(cleaned);
        if (!isNaN(numValue) && numValue > 0) {
          const formatted = numValue.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
          setDisplayValue(formatted);
        }
      }
      if (onBlur) {
        onBlur(e);
      }
    };

    return (
      <Input
        ref={ref}
        type="text"
        inputMode="decimal"
        placeholder={placeholder}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        {...props}
      />
    );
  }
);

CurrencyInput.displayName = 'CurrencyInput';